import React, { useState } from "react";
import { Container, Group, Anchor, Text, Modal, Button } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";

export function DocsFooter() {
  const { width } = useViewportSize();
  const isMobile = width < 768; // Adjust this breakpoint as needed
  const currentPath = window.location.pathname;

  const docsLabel = isMobile ? "Docs" : "Documentation";

  // Modal state for Sign Out confirmation
  const [modalOpened, setModalOpened] = useState(false);

  // Extract CSRF token from the meta tag
  const csrfToken =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || "";

  const handleSignOut = () => {
    const form = document.getElementById("sign-out-form") as HTMLFormElement;
    form?.submit();
  };

  const links = [
    currentPath !== "/node" && { link: "/terms", label: "Terms" },
    currentPath !== "/node" && { link: "/privacy", label: "Privacy" },
    { link: "/safety", label: "Safety" },
    { link: "/docs", label: docsLabel },
    ...(currentPath === "/node"
      ? [
          { link: "/nodes", label: "My Nodes" },
          { link: "/logout", label: "Sign Out", post: true },
        ]
      : []),
  ].filter(Boolean); // Filter out any `null` or `false` values

  const items = links.map((link) =>
    link.post ? (
      <button
        key={link.label}
        style={linkButtonStyles}
        onClick={() => setModalOpened(true)}
      >
        {link.label}
      </button>
    ) : (
      <Anchor<"a">
        color="dimmed"
        key={link.label}
        href={link.link}
        style={linkStyles}
        size="sm"
      >
        {link.label}
      </Anchor>
    )
  );

  return (
    <>
      <footer style={footerStyles}>
        <div style={containerStyles}>
          <Container size="md">
            <Group gap="md">{items}</Group>
            <Text size="xs" style={textStyles}>
              &copy; 2026 Rizful
            </Text>
          </Container>
        </div>
      </footer>

      {/* Modal for Sign Out Confirmation */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title="Confirm Sign Out"
        size="sm"
      >
        <Text>
          Are you sure you want to sign out? You will need to log in again to
          access your account.
        </Text>
        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setModalOpened(false)}>
            Cancel
          </Button>
          <form
            id="sign-out-form"
            action="/logout"
            method="POST"
            style={{ margin: 0 }}
          >
            <input type="hidden" name="authenticity_token" value={csrfToken} />
            <Button color="red" type="button" onClick={handleSignOut}>
              Yes, Sign Out
            </Button>
          </form>
        </Group>
      </Modal>
    </>
  );
}

// Inline styles
const footerStyles: React.CSSProperties = {
  marginTop: "120px",
  paddingTop: "20px",
  paddingBottom: "20px",
  borderTop:
    "1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5))",
};

const containerStyles: React.CSSProperties = {
  display: "flex",
  flexDirection: "column" as "column",
  alignItems: "center" as "center",
  justifyContent: "center" as "center",
};

const linkStyles: React.CSSProperties = {
  color: "light-dark(var(--mantine-color-gray-7), var(--mantine-color-dark-0))",
  padding: "0 8px",
  textDecoration: "none",
};

const linkButtonStyles: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "light-dark(var(--mantine-color-gray-7), var(--mantine-color-dark-0))",
  cursor: "pointer",
  fontSize: "14px",
  padding: "0 8px",
  textDecoration: "none",
};

const textStyles: React.CSSProperties = {
  marginTop: "20px",
  textAlign: "center" as "center",
  color: "var(--mantine-color-gray-6)",
};

export default DocsFooter;
